import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  Form,
  SelectPicker,
  Button,
  Stack,
  IconButton,
  Message,
  useToaster,
  List,
  InputNumber,
  Accordion
} from 'rsuite';
import PlusIcon from '@rsuite/icons/Plus';
import MinusIcon from '@rsuite/icons/Minus';
import { RiUserLocationLine } from 'react-icons/ri';
import { useAppDispatch, useAppSelector } from '@/config/hooks';
import { updateUserAccessPriority, getZonePrioritiesWithUsers } from '@/services/access.service';
import { getFloors } from '@/services/floor.service';
import { getZones } from '@/services/zone.service';

const slimText = {
  fontSize: '0.9em',
  color: '#97969B',
  fontWeight: 'lighter',
  paddingBottom: 5
};

const titleStyle = {
  fontSize: '1.1em',
  fontWeight: 600,
  color: '#333',
  margin: 0
};

const subTitleStyle = {
  fontSize: '1em',
  fontWeight: 500,
  color: '#666',
  marginLeft: 30
};

// Helper function to transform initialData - moved outside component to avoid dependencies
function transformInitialData(priorities) {
  if (!Array.isArray(priorities) || priorities.length === 0) {
    console.warn('Invalid priorities data:', priorities);
    return [{ zone: null, data: [{ dayOfWeek: null, priority: null }] }];
  }

  const grouped = priorities.reduce((acc, curr) => {
    const zoneId = curr.zone?._id || curr.zone;

    if (!zoneId) {
      console.warn('Priority missing zone ID:', curr);
      return acc;
    }

    if (!acc[zoneId]) {
      acc[zoneId] = {
        zone: zoneId,
        data: []
      };
    }

    acc[zoneId].data.push({
      dayOfWeek: Number(curr.dayOfWeek),
      priority: Number(curr.priority),
      _id: curr._id
    });

    return acc;
  }, {});

  return Object.values(grouped);
}

const BasePriorityDrawer = ({ open, onClose, users, initialData, isEdit, zones: propZones }) => {
  const dispatch = useAppDispatch();
  const toaster = useToaster();
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { t } = useTranslation();

  // Get floors with sort order
  const floors = useAppSelector(state => state.floor?.data || [])?.map(item => ({
    ...item,
    value: item._id,
    label: item.name,
    // Add a sorting value - negative for basement floors, positive for above-ground floors
    sortOrder:
      item.name.toLowerCase().includes('parking') || item.name.startsWith('-')
        ? -parseInt(item.name.match(/\d+/)?.[0] || '0')
        : parseInt(item.name.match(/\d+/)?.[0] || '0')
  }));

  const daysOfWeek = [
    { label: t('Monday'), value: 1 },
    { label: t('Tuesday'), value: 2 },
    { label: t('Wednesday'), value: 3 },
    { label: t('Thursday'), value: 4 },
    { label: t('Friday'), value: 5 },
    { label: t('Saturday'), value: 6 },
    { label: t('Sunday'), value: 7 }
  ];

  const storeZones = useAppSelector(state => state.zone?.data || []);
  const allZones = propZones || storeZones;

  // Memoize zones to prevent recreation on every render
  const zones = useMemo(() => {
    return allZones
      ?.map(item => {
        const floorObj = floors.find(floor => floor.value === item.floor);
        return {
          value: item._id,
          label: `${floorObj?.building?.address || 'building'} / ${
            floorObj?.label || 'Unknown Floor'
          } / ${item.name}`,
          floor: item.floor,
          sortOrder: floorObj?.sortOrder || 0
        };
      })
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [allZones, floors]);

  useEffect(() => {
    dispatch(getFloors());
    dispatch(getZones());
  }, [dispatch]);

  const [formValue, setFormValue] = useState({
    User: initialData?.user?._id || ''
  });

  // Initialize local access list
  const [localAccessList, setLocalAccessList] = useState([
    {
      zone: null,
      data: [{ dayOfWeek: null, priority: null }]
    }
  ]);

  // Extract initialData ID for dependency tracking
  const initialDataId = initialData?.user?._id || null;

  // Reset form when initialData changes (when drawer opens with new data)
  useEffect(() => {
    if (initialData) {
      setFormValue({
        User: initialData.user?._id || ''
      });

      if (initialData.priorities?.length > 0) {
        const transformedData = transformInitialData(initialData.priorities);

        // Sort the transformed data by floor order
        const sortedData = [...transformedData].sort((a, b) => {
          const zoneA = zones.find(z => z.value === a.zone);
          const zoneB = zones.find(z => z.value === b.zone);
          return (zoneA?.sortOrder || 0) - (zoneB?.sortOrder || 0);
        });

        setLocalAccessList(sortedData);
      } else if (isEdit) {
        // For edit mode with no priorities, start with empty list
        setLocalAccessList([
          {
            zone: null,
            data: [{ dayOfWeek: null, priority: null }]
          }
        ]);
      }
    }
    // Use stable dependencies to prevent infinite loops
  }, [initialDataId, isEdit, open]);
  const validateForm = () => {
    if (!formValue.User) {
      toaster.push(<Message type="error">{t('Please select a user')}</Message>, {
        placement: 'topCenter'
      });
      return false;
    }

    if (localAccessList.some(item => !item.zone)) {
      toaster.push(
        <Message type="error">{t('Please select a zone for each access row')}</Message>,
        {
          placement: 'topCenter'
        }
      );
      return false;
    }

    const invalidPriorities = localAccessList.some(item =>
      item.data.some(d => !d.dayOfWeek || d.priority === null || d.priority === undefined)
    );

    if (invalidPriorities) {
      toaster.push(
        <Message type="error">{t('Please fill in all day and priority values')}</Message>,
        {
          placement: 'topCenter'
        }
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setIsSaving(true);
      // Format the data exactly as the backend expects it
      const priorityConfigs = localAccessList.flatMap(({ zone, data }) =>
        data.map(({ dayOfWeek, priority, _id }) => ({
          zone,
          dayOfWeek: parseInt(dayOfWeek),
          priority: parseInt(priority),
          _id
        }))
      );

      console.log('Submitting priority configs:', priorityConfigs);

      const response = await dispatch(updateUserAccessPriority(formValue.User, priorityConfigs));

      if (response?.status === 200) {
        dispatch(getZonePrioritiesWithUsers());

        toaster.push(
          <Message type="success">
            {isEdit ? t('Priority updated successfully') : t('Priority added successfully')}
          </Message>,
          { placement: 'topCenter' }
        );

        onClose();
      }
    } catch (error) {
      console.error('Error updating priority:', error);
      toaster.push(
        <Message type="error">
          {t(error?.response?.data?.message) || t(error?.message) || t('Operation failed')}
        </Message>,
        { placement: 'topCenter' }
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddRow = indexRow => {
    setLocalAccessList(prevList => {
      return prevList.map((item, i) => {
        if (i !== indexRow) return item;
        return {
          ...item,
          data: [...item.data, { dayOfWeek: null, priority: null }]
        };
      });
    });
  };

  const handleAddZoneRow = () => {
    setLocalAccessList(prevList => [
      ...prevList,
      { zone: null, data: [{ dayOfWeek: null, priority: null }] }
    ]);
  };

  const handleDeleteZoneRow = indexRow => {
    setLocalAccessList(prevList => prevList.filter((_, index) => index !== indexRow));
  };

  const handleRemoveRow = (indexRow, indexData) => {
    setLocalAccessList(prevList => {
      return prevList.map((item, i) => {
        if (i !== indexRow) return item;
        return {
          ...item,
          data: item.data.filter((_, j) => j !== indexData)
        };
      });
    });
  };

  const handleChange = (indexRow, field, value, indexData = null) => {
    setLocalAccessList(prevList => {
      return prevList.map((item, i) => {
        if (i !== indexRow) return item;

        if (field === 'zone') {
          return {
            ...item,
            zone: value
          };
        }

        if (indexData !== null) {
          return {
            ...item,
            data: item.data.map((dataItem, j) => {
              if (j !== indexData) return dataItem;
              return {
                ...dataItem,
                [field]: value
              };
            })
          };
        }

        return item;
      });
    });
  };

  return (
    <Drawer backdrop="static" size="md" placement="right" onClose={onClose} open={open}>
      <Drawer.Header>
        <Drawer.Title>
          {isEdit ? 'Edit' : 'Add'} {t('User Access Priority')}
        </Drawer.Title>
        <Drawer.Actions>
          <Button
            onClick={handleSubmit}
            appearance="primary"
            loading={isSaving}
            disabled={isSaving}
          >
            {t('Save')}
          </Button>
          <Button onClick={onClose} appearance="subtle" disabled={isSaving}>
            {t('Cancel')}
          </Button>
        </Drawer.Actions>
      </Drawer.Header>
      <Drawer.Body>
        <Form fluid>
          <Form.Group>
            <Form.ControlLabel>{t('User')}</Form.ControlLabel>
            <SelectPicker
              block
              data={users.map(user => ({
                label: `${user.firstname} ${user.lastname}`,
                value: user._id
              }))}
              value={formValue.User}
              onChange={value => setFormValue({ ...formValue, User: value })}
              disabled={isEdit}
              searchable
              cleanable={false}
              placeholder={t('Select User')}
            />
          </Form.Group>

          <Stack style={{ marginTop: 50, marginBottom: 10, marginLeft: 10 }} spacing={5}>
            <RiUserLocationLine />
            <h6 style={titleStyle}>{t('Manage accesses with priority')}</h6>
          </Stack>

          <div className="ml-10">
            <Accordion>
              {localAccessList.map((row, rowIndex) => (
                <Accordion.Panel
                  key={`zone-row-${rowIndex}`}
                  header={
                    <Stack spacing={10} alignItems="flex-start">
                      {row.zone ? (
                        <span style={subTitleStyle}>
                          {zones.find(z => z.value === row.zone)?.label || 'No zone selected'}
                        </span>
                      ) : (
                        <span style={subTitleStyle}>{t('New Zone Access')}</span>
                      )}
                    </Stack>
                  }
                >
                  <Stack justifyContent="space-between">
                    <div style={{ textAlign: 'left', marginLeft: '28px' }}>
                      <div style={slimText}>Zone *</div>
                      <SelectPicker
                        data={zones}
                        value={row.zone}
                        onChange={value => handleChange(rowIndex, 'zone', value)}
                        style={{ width: 250 }}
                        placement="auto"
                        disabledItemValues={localAccessList
                          .filter((_, i) => i !== rowIndex)
                          .map(item => item.zone)
                          .filter(Boolean)}
                        cleanable={false}
                        placeholder={t('Select Zone')}
                      />
                    </div>
                    <IconButton
                      appearance="subtle"
                      size="sm"
                      icon={<MinusIcon color="red" />}
                      onClick={() => handleDeleteZoneRow(rowIndex)}
                      disabled={localAccessList.length === 1}
                    />
                  </Stack>

                  <List style={{ marginLeft: 50, marginTop: 20 }}>
                    {row.data
                      .sort((a, b) => (a.dayOfWeek || 0) - (b.dayOfWeek || 0))
                      .map((item, index) => (
                        <List.Item key={`day-item-${rowIndex}-${index}`}>
                          <Stack spacing={20} wrap>
                            <div style={{ textAlign: 'left' }}>
                              <div style={slimText}>{t('Day of week *')}</div>
                              <SelectPicker
                                data={daysOfWeek}
                                value={item.dayOfWeek}
                                onChange={value =>
                                  handleChange(rowIndex, 'dayOfWeek', value, index)
                                }
                                style={{ width: 250 }}
                                placement="auto"
                                disabledItemValues={row.data
                                  .filter((_, i) => i !== index)
                                  .map(item => item.dayOfWeek)
                                  .filter(Boolean)}
                                placeholder={t('Select Day')}
                              />
                            </div>

                            <div style={{ textAlign: 'left' }}>
                              <div style={slimText}>{t('Priority *')}</div>
                              <InputNumber
                                value={item.priority}
                                onChange={value => handleChange(rowIndex, 'priority', value, index)}
                                style={{ width: 100 }}
                                min={1}
                              />
                            </div>

                            <Stack spacing={5} alignItems="flex-end">
                              <IconButton
                                onClick={() => handleRemoveRow(rowIndex, index)}
                                icon={<MinusIcon />}
                                size="xs"
                                disabled={row.data.length === 1}
                              />
                              {index === row.data.length - 1 && (
                                <IconButton
                                  onClick={() => handleAddRow(rowIndex)}
                                  icon={<PlusIcon />}
                                  size="xs"
                                />
                              )}
                            </Stack>
                          </Stack>
                        </List.Item>
                      ))}
                  </List>
                </Accordion.Panel>
              ))}
            </Accordion>

            <IconButton
              onClick={handleAddZoneRow}
              icon={<PlusIcon color="var(--rs-primary-800)" />}
              style={{ marginTop: 10 }}
            />
          </div>
        </Form>
      </Drawer.Body>
    </Drawer>
  );
};

const EditPrioDrawer = props => <BasePriorityDrawer {...props} isEdit={true} />;

export default EditPrioDrawer;
